import React, { Component, Fragment } from "react";
import { FormControl, FormControlLabel, Radio, RadioGroup, Typography, Tooltip, Button, IconButton } from "@material-ui/core";
import Swal from "sweetalert2";
import moment from "moment";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import CalculatorIcon from "../../../layout/CalculatorIcon";
import CustomDialog from "../../../layout/CustomDialog";
import helpTexts from "../../../../assets/texts/helpTexts";
import LabeledTextField from "../../../layout/LabeledTextField";
import { convertDecimalToFraction } from "../../../../services/utils/utils";

// Original optional props:
// open
// onClose

// Extended optional props:
// handleResults - force the result.
// soldPropertyDate
// deduction
// getPurchaseDate

const depreciationPercentOpts = [
  { label: "1.5%", value: 0.015, fullLabel: "בניני אבן איתנים - 1.50%" },
  { label: "2%", value: 0.02, fullLabel: "בנייני בטון מזוין - 2%" },
  { label: "4%", value: 0.04, fullLabel: "בניינים שבנייתם איתנה פחות - 4%" },
  { label: "6%", value: 0.06, fullLabel: "בניינים שבנייתם גרועה - 6%" },
  { label: "אחר", value: -1, fullLabel: "שיעור מס אחר" }
];
const depreciationBuiltPercentOpts = [
  { label: "100%", value: 1 },
  { label: "4/5", value: 0.8 },
  { label: "3/4", value: 0.75 },
  { label: "2/3", value: parseFloat((2 / 3).toFixed(3)) },
  { label: "1/2", value: 0.5 },
  { label: "1/3", value: parseFloat((1 / 3).toFixed(3)) },
  { label: "1/4", value: 0.25 },
  { label: "1/5", value: 0.2 },
  { label: "אחר", value: -1 }
];

const renderChoicePills = ({ options, name, value, isDisabled, isNumeric, onChange }) => (
  <FormControl component="fieldset" disabled={isDisabled}>
    <RadioGroup
      row
      name={name}
      value={String(value)}
      onChange={(e) => {
        const nextValue = isNumeric ? Number(e.target.value) : e.target.value;
        onChange({ target: { name, value: nextValue } });
      }}
    >
      {options.map((option) => {
        const control = (
          <FormControlLabel
            value={String(option.value)}
            control={<Radio color="primary" />}
            label={option.label}
            labelPlacement="end"
          />
        );
        if (!option.fullLabel) return <Fragment key={String(option.value)}>{control}</Fragment>;
        return (
          <Tooltip key={String(option.value)} title={option.fullLabel}>
            <span>{control}</span>
          </Tooltip>
        );
      })}
    </RadioGroup>
  </FormControl>
);

class DepreciationCalculatorDialog extends Component {
  state = {
    depType: this.props.propertyType !== "otherProperty" ? "ptor" : "regTax",
    // depType: this.props.sellerType === "single" && this.props.propertyType === "dmm" ? "ptor" : "regTax",
    dateRanges: [{ startDate: "", endDate: "" }],
    depreciationPercentFromList: 0.02,
    depreciationPercentTemp: null,
    depreciationBuiltPercentFromList: 1,
    depreciationBuiltPercentNumerator: null,
    depreciationBuiltPercentDenominator: null,
    depreciationResult: null
  };

  componentDidMount() {
    if (this.props.deduction.depreciation) {
      const newState = { ...this.state };
      const dep = this.props.deduction.depreciation;
      if (dep.depType) newState.depType = dep.depType;
      if (Array.isArray(dep.dateRanges) && dep.dateRanges.length > 0) {
        const newDateRange = [];
        for (const range of dep.dateRanges) {
          newDateRange.push({ ...range });
        }
        newState.dateRanges = newDateRange;
      } else if (this.props.propertyType === "otherProperty") {
        const startDate =
          this.props.deduction.deduType === "inheritance"
            ? this.props.getPurchaseDate(this.props.deduction)
            : this.props.deduction.deduDate;
        const endDate = this.props.soldPropertyDate;
        newState.dateRanges = [{ startDate, endDate }];
      }
      if (dep.depreciationPercent) {
        newState.depreciationPercentFromList = depreciationPercentOpts.map((item) => item.value).includes(dep.depreciationPercent)
          ? dep.depreciationPercent
          : -1;
        if (newState.depreciationPercentFromList === -1) {
          newState.depreciationPercentTemp = dep.depreciationPercent;
        }
      }

      if (dep.depreciationBuiltPercent) {
        newState.depreciationBuiltPercentFromList = depreciationBuiltPercentOpts
          .map((item) => item.value)
          .includes(dep.depreciationBuiltPercent)
          ? dep.depreciationBuiltPercent
          : -1;
        if (newState.depreciationBuiltPercentFromList === -1) {
          const depreciationBuiltPercent = convertDecimalToFraction(dep.depreciationBuiltPercent);
          newState.depreciationBuiltPercentNumerator = depreciationBuiltPercent[0];
          newState.depreciationBuiltPercentDenominator = depreciationBuiltPercent[1];
        }
      }

      if (dep.depreciationBuiltPercentFromList) newState.depreciationBuiltPercentFromList = dep.depreciationBuiltPercentFromList;
      if (dep.depreciationBuiltPercentNumerator) newState.depreciationBuiltPercentNumerator = dep.depreciationBuiltPercentNumerator;
      if (dep.depreciationBuiltPercentDenominator) newState.depreciationBuiltPercentDenominator = dep.depreciationBuiltPercentDenominator;

      if (dep.depreciationResult) newState.depreciationResult = dep.depreciationResult;
      this.setState(newState);
    }
  }

  render() {
    const isTaxTrackDisabled = this.props.propertyType === "otherProperty";
    const isRegularTax = this.state.depType === "regTax";
    const depTypeOpts = [{ label: "שעור מס רגילים", value: "regTax" }];
    if (!isTaxTrackDisabled) {
      // if (this.props.sellerType === "single" && this.props.propertyType === "dmm") {
      depTypeOpts.unshift({ label: "10%", value: "percent10" });
      depTypeOpts.unshift({ label: "פטור", value: "ptor" });
    }

    return (
      <CustomDialog
        label="מחשבון פחת"
        open={this.props.open}
        onClose={this.props.onClose}
        maxWidth="md"
        paperClassName="deal-calc-dialog deal-depreciation-dialog"
        paperStyle={{ width: 720, maxWidth: "calc(100vw - 64px)" }}
        isContentDivided={false}
        titleAlign="center"
      >
        <div className="deal-depreciation-dialog-body">
          <div className="deal-depreciation-dialog-section">
            <div className="deal-depreciation-dialog-row">
              <Typography className="deal-depreciation-dialog-label">מסלול מיסוי הכנסה משכירות</Typography>
              {renderChoicePills({
                options: depTypeOpts,
                name: "depType",
                value: this.state.depType,
                isDisabled: isTaxTrackDisabled,
                onChange: (e) => this.handleOnChange(e)
              })}
            </div>
          </div>

          {isRegularTax ? null : (
            <div className="deal-depreciation-dialog-section">
              <div className="deal-depreciation-dialog-row">
                <div className="deal-depreciation-dialog-field-group">
                  <LabeledTextField
                    type="select"
                    value={this.state.depreciationBuiltPercentFromList}
                    labelWidth={135}
                    textFieldWidth={130}
                    name="depreciationBuiltPercentFromList"
                    options={depreciationBuiltPercentOpts}
                    label="שיעור חלק המבנה"
                    onChange={(e) => this.handleOnChange(e)}
                  />
                  {this.state.depreciationBuiltPercentFromList === -1 ? (
                    <Fragment>
                      <LabeledTextField
                        type="number"
                        value={this.state.depreciationBuiltPercentDenominator}
                        textFieldWidth={70}
                        max={1000}
                        min={0}
                        name="depreciationBuiltPercentDenominator"
                        currency="/"
                        onChange={(e) => this.handleOnChange(e)}
                      />
                      <LabeledTextField
                        type="number"
                        value={this.state.depreciationBuiltPercentNumerator}
                        textFieldWidth={60}
                        max={1000}
                        min={0}
                        name="depreciationBuiltPercentNumerator"
                        onChange={(e) => this.handleOnChange(e)}
                      />
                    </Fragment>
                  ) : null}
                </div>
                <div className="deal-depreciation-dialog-field-group">
                  <Typography className="deal-depreciation-dialog-label">שיעור פחת</Typography>
                  {renderChoicePills({
                    options: depreciationPercentOpts,
                    name: "depreciationPercentFromList",
                    value: this.state.depreciationPercentFromList,
                    isNumeric: true,
                    onChange: (e) => this.handleOnChange(e)
                  })}
                  {this.state.depreciationPercentFromList === -1 ? (
                    <LabeledTextField
                      type="number"
                      value={this.state.depreciationPercentTemp}
                      textFieldWidth={90}
                      min={0}
                      max={100}
                      name="depreciationPercentTemp"
                      currency="%"
                      onChange={(e) => this.handleOnChange(e)}
                    />
                  ) : null}
                </div>
              </div>
              <div className="deal-depreciation-dialog-row">
                <LabeledTextField
                  type="number"
                  value={this.props.deduction.amount}
                  labelWidth={200}
                  textFieldWidth={170}
                  disabled={true}
                  label="סכום הרכישה/ההשבחה"
                />
              </div>
            </div>
          )}

          <div className="deal-depreciation-dialog-section">
            {this.state.dateRanges.map((range, index) => (
              <div className="deal-depreciation-dialog-row is-dates" key={`date-ranges-${index}`}>
                <div className="deal-depreciation-dialog-date-fields">
                  <Typography className="deal-depreciation-dialog-label">הושכר מתאריך</Typography>
                  <LabeledTextField
                    type="date"
                    value={range.startDate}
                    textFieldWidth={130}
                    name="startDate"
                    onChange={(e) => this.handleDateRangeOnChange(e, index)}
                  />
                  <Typography className="deal-depreciation-dialog-label">עד תאריך</Typography>
                  <LabeledTextField
                    type="date"
                    value={range.endDate}
                    textFieldWidth={130}
                    name="endDate"
                    onChange={(e) => this.handleDateRangeOnChange(e, index)}
                  />
                </div>
                <div className="deal-depreciation-dialog-date-actions">
                  <Tooltip title="הוספה">
                    <IconButton className="deal-depreciation-dialog-add" size="small" aria-label="הוספה" onClick={() => this.addDateRange()}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="מחיקה">
                    <IconButton
                      className="deal-depreciation-dialog-delete"
                      size="small"
                      aria-label="מחיקה"
                      onClick={() => this.removeDateRange(index)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>

          {isRegularTax ? (
            <div className="deal-depreciation-dialog-section">
              <Typography className="deal-depreciation-dialog-notice">נא למלא תאריך וסכום הפחת בהתאם לטופס י"א מרו"ח</Typography>
              <div className="deal-depreciation-dialog-row">
                <LabeledTextField
                  type="number"
                  value={this.state.depreciationResult}
                  labelWidth={200}
                  textFieldWidth={170}
                  min={0}
                  name="depreciationResult"
                  currency="₪"
                  onChange={(e) => this.handleOnChange(e)}
                />
              </div>
            </div>
          ) : (
            <div className="deal-depreciation-dialog-section">
              <div className="deal-depreciation-dialog-row">
                <LabeledTextField
                  type="number"
                  value={
                    this.props.deduction.depreciation
                      ? this.props.deduction.depreciation.depreciationResult
                      : null
                  }
                  labelWidth={160}
                  textFieldWidth={170}
                  disabled={true}
                  helpDialog={helpTexts.depreciationResult}
                  label="סכום הפחת לניכוי"
                />
              </div>
            </div>
          )}

          <div className="deal-depreciation-dialog-footer">
            <Button className="deal-depreciation-dialog-clear" variant="contained" onClick={() => this.clearDepreciation()}>
              מחק נתונים
            </Button>
            <Button className="deal-depreciation-dialog-save" variant="contained" onClick={() => this.calculateDepreciation()}>
              {isRegularTax ? null : <CalculatorIcon />}
              {isRegularTax ? "הוסף" : "חשב"}
            </Button>
          </div>
        </div>
      </CustomDialog>
    );
  }

  calculateDepreciation() {
    const today = moment(new Date()).format("YYYY-MM-DD");
    for (let i = 0; i < this.state.dateRanges.length; i++) {
      const range = this.state.dateRanges[i];
      if (!range.startDate) return Swal.fire("", "תאריך התחלה הינו שדה חובה", "error");
      if (!range.endDate) return Swal.fire("", "תאריך סיום הינו שדה חובה", "error");
      if (range.endDate > today) return Swal.fire("", "תאריך סיום לא יכול להיות תאריך עתידי", "error");
      if (range.startDate > range.endDate) return Swal.fire("", "תאריך התחלה לא יכול להיות לאחר תאריך סיום", "error");
      const deduDate =
        this.props.deduction.deduType === "inheritance" ? this.props.getPurchaseDate(this.props.deduction) : this.props.deduction.deduDate;
      if (range.startDate < deduDate) return Swal.fire("", "תאריך ההתחלה לא יכול להיות לפני תאריך הניכוי", "error");
      if (range.endDate > this.props.soldPropertyDate) return Swal.fire("", "תאריך הסיום לא יכול להיות לאחר תאריך המכירה", "error");
      if (i > 0) {
        if (range.startDate <= this.state.dateRanges[i - 1].endDate)
          return Swal.fire("", "טווחי התאריכים צריכים להיות לפי סדר ובלי חפיפה ביניהם", "error");
      }
    }
    if (this.state.depType === "regTax") {
      if (this.state.depreciationResult) {
        const depreciation = {
          depType: this.state.depType,
          depCurrency: "newShekel",
          // depCurrency: this.props.deduction.deduCurrency,
          dateRanges: this.state.dateRanges,
          depreciationPercent: null,
          depreciationBuiltPercent: null,
          depreciationResult: this.state.depreciationResult
        };
        this.applyDepreciationResult(depreciation);
      }
    } else {
      let depreciationResult = null;
      if (!this.props.deduction.amount) {
        return Swal.fire("", "סכום ההשבחה ריק", "error");
      } else if (
        this.state.depreciationBuiltPercentFromList === -1 &&
        (!this.state.depreciationBuiltPercentDenominator || !this.state.depreciationBuiltPercentNumerator)
      ) {
        return Swal.fire("", "שיעור חלק המבנה במקרקעין הינו שדה חובה", "error");
      } else if (this.state.depreciationPercentFromList === -1 && !this.state.depreciationPercentTemp) {
        return Swal.fire("", "שיעור הפחת הינו שדה חובה", "error");
      } else {
        const depreciationPercent =
          this.state.depreciationPercentFromList === -1
            ? this.state.depreciationPercentTemp / 100 || 0
            : this.state.depreciationPercentFromList;
        const depreciationBuiltPercent =
          this.state.depreciationBuiltPercentFromList === -1
            ? parseFloat((this.state.depreciationBuiltPercentNumerator / this.state.depreciationBuiltPercentDenominator).toFixed(3))
            : this.state.depreciationBuiltPercentFromList;
        let totalYears = 0;
        for (const range of this.state.dateRanges) {
          const start =
            this.state.depType === "ptor" && range.startDate < "2007-02-27" ? new Date("2007-02-27") : new Date(range.startDate);
          const end = new Date(range.endDate);
          // Includes the starting and ending date in the range.
          end.setDate(end.getDate() + 1);
          totalYears += Math.max(0, moment(end).diff(moment(start), "years", true));
        }
        depreciationResult = this.props.deduction.amount * depreciationPercent * depreciationBuiltPercent * totalYears;
        if (Number.isFinite(depreciationResult)) {
          const depreciation = {
            depType: this.state.depType,
            depCurrency: this.props.deduction.deduCurrency,
            dateRanges: this.state.dateRanges,
            depreciationPercent,
            depreciationBuiltPercent,
            depreciationResult
          };
          this.applyDepreciationResult(depreciation);
        }
      }
    }
  }

  applyDepreciationResult(depreciation) {
    if (this.props.handleResults) {
      this.props.handleResults({ target: { value: depreciation, name: "depreciation" } });
    }
    if (this.props.onAdded) {
      this.props.onAdded();
      return;
    }
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  clearDepreciation() {
    this.props.handleResults({ target: { value: {}, name: "depreciation" } });
    // this.props.onClose();
  }

  handleOnChange(e) {
    const newState = { ...this.state, [e.target.name]: e.target.value };
    this.handleDependencies(e, newState);
    this.setState(newState);
  }

  handleDateRangeOnChange(e, index) {
    const dateRanges = [...this.state.dateRanges];
    dateRanges[index][e.target.name] = e.target.value;
    this.setState({ dateRanges });
  }

  addDateRange() {
    const dateRanges = [...this.state.dateRanges];
    dateRanges.push({ startDate: "", endDate: "" });
    this.setState({ dateRanges });
  }

  removeDateRange(index) {
    let dateRanges = this.state.dateRanges.filter((range, rangeIndex) => index !== rangeIndex);
    if (dateRanges.length === 0) {
      dateRanges = [{ startDate: "", endDate: "" }];
    }
    this.setState({ dateRanges });
  }

  handleDependencies(e, newState) {
    if (e.target.name === "depType") {
      newState.dateRanges = [{ startDate: "", endDate: "" }];
      if (e.target.value === "regTax") {
        newState.depreciationPercentFromList = 0.02;
        newState.depreciationPercentTemp = null;
        newState.depreciationBuiltPercentFromList = 1;
        newState.depreciationBuiltPercentDenominator = null;
        newState.depreciationBuiltPercentNumerator = null;
        newState.depreciationResult = null;
      }
    } else if (e.target.name === "depreciationPercentFromList") {
      if (e.target.value !== -1) {
        newState.depreciationPercentTemp = null;
      }
    } else if (e.target.name === "depreciationBuiltPercentFromList") {
      if (e.target.value !== -1) {
        newState.depreciationBuiltPercentDenominator = null;
        newState.depreciationBuiltPercentNumerator = null;
      }
    }
  }
}

export default DepreciationCalculatorDialog;
